import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useBreadcrumbs } from "@/app-shell";
import { useReports } from "@/profitability/report-query";
import {
  PROFITABILITY_PATH,
  profitabilityRunPath,
  profitabilityTemplatePath,
  readNewRunPreset,
} from "@/profitability/routes";
import { formatReportPeriod, reportError } from "@/profitability/utils";
import { NewRunForm, NewRunSkeleton } from "./new-run-form";

export const NewRunPage = ({
  businessId,
  reportId,
}: {
  businessId: string;
  reportId: string;
}) => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const preset = readNewRunPreset(search);
  const reports = useReports(businessId);
  const report = reports.data?.find((candidate) => candidate.id === reportId);
  useBreadcrumbs([
    { label: "Job Costing", href: PROFITABILITY_PATH },
    {
      label: report?.name ?? "Agent",
      href: profitabilityTemplatePath(reportId),
    },
    { label: "Upload files" },
  ]);
  return (
    <PageContainer>
      <PageHeader
        title="Upload files"
        description={
          report && preset
            ? `Add what's missing from ${formatReportPeriod(preset)}. Its other files are already in place.`
            : "Files for a new period, or files that fill in or correct a period that already ran."
        }
      />
      <Stack gap={6} className="max-w-3xl">
        {reports.isPending && <NewRunSkeleton label="Loading agent" />}
        {reports.isError && (
          <Stack gap={3} align="start">
            <Text role="alert" tone="destructive">
              The agent could not load. {reportError(reports.error)} Try again.
            </Text>
            <Button variant="secondary" onClick={() => void reports.refetch()}>
              Try again
            </Button>
          </Stack>
        )}
        {reports.isSuccess && !report && (
          <Text>
            This agent does not exist or belongs to another business. Return to
            Job Costing to choose another.
          </Text>
        )}
        {report && !report.published && (
          <Text tone="muted">
            Review and publish the agent before starting a run.
          </Text>
        )}
        {report?.published && (
          <NewRunForm
            businessId={businessId}
            report={report}
            preset={preset}
            onCreated={(id) => navigate(profitabilityRunPath(id))}
            onCancel={() => navigate(profitabilityTemplatePath(reportId))}
          />
        )}
      </Stack>
    </PageContainer>
  );
};
